import { useState, useEffect, useCallback, useRef } from "react";
import { ChefHat, Check, Truck, UtensilsCrossed, Clock, RefreshCw, Volume2, VolumeX, MapPin, Printer, Settings, Wifi, WifiOff, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import API_URL from "@/utils/api";
import PrintService from "@/services/PrintService";
import PrinterSettings from "@/components/PrinterSettings";

const API = API_URL;

// Sound for new orders
const playNotificationSound = () => {
  const audio = new Audio("/sounds/ubereats_ping.mp3");
  audio.volume = 1.0; // Max volume for kitchen
  audio.play().catch((err) => {
    console.warn("Autoplay blocked or audio error:", err);
    toast.error("El navegador bloqueó el sonido. Haga clic en cualquier lugar para activarlo.", {
      id: "audio-blocked",
      duration: 5000
    });
  });
};

// Max time to show is 35 minutes
const MAX_TIME_MINUTES = 35;

// Local storage keys for settings
const SETTINGS_KEY = "kitchen_settings";

export default function AdminKitchen() {
  const [preparingOrders, setPreparingOrders] = useState([]);
  const [readyOrders, setReadyOrders] = useState([]);
  const [onTheWayOrders, setOnTheWayOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const { user } = useAuth();

  // Print settings
  const [showPrintSettings, setShowPrintSettings] = useState(false);
  const [qzConnected, setQzConnected] = useState(false);

  // Refs for tracking
  const printedOrdersRef = useRef(new Set());
  const lastOrderCountRef = useRef(0);
  const lastOrderIdsRef = useRef(new Set());
  const soundEnabledRef = useRef(soundEnabled);
  const autoPrintEnabledRef = useRef(autoPrintEnabled);

  // Load settings from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SETTINGS_KEY);
      if (saved) {
        const settings = JSON.parse(saved);
        setSoundEnabled(settings.soundEnabled ?? true);
        setAutoPrintEnabled(settings.autoPrintEnabled ?? false);
        if (settings.printedOrders) {
          printedOrdersRef.current = new Set(settings.printedOrders);
        }
      }
    } catch (e) {
      console.error("Error loading kitchen settings:", e);
    }

    // Try to connect to QZ Tray on load
    checkQzConnection();
  }, []);

  // Save settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify({
        soundEnabled,
        autoPrintEnabled,
        printedOrders: Array.from(printedOrdersRef.current).slice(-50)
      }));
    } catch (e) {
      console.error("Error saving kitchen settings:", e);
    }
  }, [soundEnabled, autoPrintEnabled]);

  // Keep refs in sync with state
  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  useEffect(() => {
    autoPrintEnabledRef.current = autoPrintEnabled;
  }, [autoPrintEnabled]);

  // Check QZ Tray connection
  const checkQzConnection = async () => {
    setQzConnected(PrintService.isQzAvailable());
  };

  // Unlock audio on first user interaction (critical for tablets)
  useEffect(() => {
    const unlockAudio = () => {
      const audio = new Audio("/sounds/ubereats_ping.mp3");
      audio.volume = 0;
      audio.play().then(() => {
        console.log("Audio unlocked by user gesture");
        window.removeEventListener('click', unlockAudio);
        window.removeEventListener('touchstart', unlockAudio);
      }).catch(e => console.log("Audio still locked:", e));
    };
    window.addEventListener('click', unlockAudio);
    window.addEventListener('touchstart', unlockAudio);
    return () => {
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/public/branches`);
      setBranches(response.data);

      // If user has assigned branch, pre-select it
      if (user?.assigned_branches?.length > 0) {
        setSelectedBranch(user.assigned_branches[0]);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };


  // Function to print a specific order
  const printOrder = async (order) => {
    try {
      await PrintService.printOrder(order, "Conexo POS");
      printedOrdersRef.current.add(order.order_id);
      toast.success(`Comanda #${order.order_id.slice(-6)} impresa`);
    } catch (error) {
      toast.error(`Error al imprimir: ${error.message}`);
    }
  };

  // Auto-print new orders
  const handleNewOrders = useCallback(async (newOrders, previousOrderIds) => {
    // La impresora debe estar activa (auto-print)
    if (!autoPrintEnabledRef.current) return;

    // Filtro 1: Pedidos que NO estén ya en la lista de impresos locales
    // Filtro 2: Pedidos que el backend diga que están PENDING de imprimir (nuevo campo)
    // Filtro 3: Si no tenemos el campo (legacy), usar IDs nuevos
    const newOrdersToPrint = newOrders.filter(order => {
      // Si el backend soporta cola (kitchen_print_status), usémoslo como verdad suprema
      if (order.kitchen_print_status === "pending") return true;
      if (order.kitchen_print_status === "printed") return false;

      // Fallback para pedidos viejos sin el campo
      return !previousOrderIds.has(order.order_id) &&
        !printedOrdersRef.current.has(order.order_id) &&
        order.status === "pending";
    });

    for (let i = 0; i < newOrdersToPrint.length; i++) {
      const order = newOrdersToPrint[i];
      // Pequeño delay escalonado para no saturar QZ
      setTimeout(async () => {
        try {
          console.log(`[PRINT-QUEUE] Imprimiendo pedido ${order.order_id}`);
          const result = await PrintService.printOrder(order, "Conexo POS");
          const success = result.success;

          if (success) {
            // 1. Marcar localmente
            printedOrdersRef.current.add(order.order_id);

            // 2. AVISAR AL BACKEND (Cola de impresión completada)
            // Esto evita doble impresión si se recarga la página
            try {
              await axios.put(
                `${API}/orders/${order.order_id}/print-status`,
                { status: "printed" },
                { withCredentials: true }
              );
            } catch (err) {
              console.warn("No se pudo actualizar print-status en backend:", err);
            }

            console.log(`[PRINT-QUEUE] Éxito: ${order.order_id}`);
          }
        } catch (error) {
          console.error(`Error auto-printing order ${order.order_id}:`, error);
        }
      }, i * 2000); // 2 segundos entre tickets
    }

    if (newOrdersToPrint.length > 0) {
      toast.info(`${newOrdersToPrint.length} comanda(s) procesadas por cola de impresión`);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const branchParam = selectedBranch && selectedBranch !== "all" ? `&branch_id=${selectedBranch}` : "";
      const [preparingRes, readyRes, onTheWayRes] = await Promise.all([
        axios.get(`${API}/kitchen/orders?status=pending,preparing${branchParam}`, { withCredentials: true }),
        axios.get(`${API}/kitchen/orders?status=ready${branchParam}`, { withCredentials: true }),
        axios.get(`${API}/kitchen/orders?status=on_the_way${branchParam}`, { withCredentials: true }),
      ]);

      const newPreparing = preparingRes.data;
      const newReady = readyRes.data;
      const newOnTheWay = onTheWayRes.data;

      const currentOrderIds = new Set(newPreparing.map(o => o.order_id));

      // Detectar nuevos pedidos comparando IDs
      // Hacemos que suene si hay CUALQUIER ID que no estuviera antes (incluido el primer arranque)
      const hasNewOrders = newPreparing.some(order => !lastOrderIdsRef.current.has(order.order_id));

      if (soundEnabledRef.current && hasNewOrders) {
        playNotificationSound();
        toast.info("¡Nuevo pedido!", { duration: 5000, icon: "🔔" });
      }

      if (lastOrderIdsRef.current.size > 0) {
        handleNewOrders(newPreparing, lastOrderIdsRef.current);
      }

      const totalOrders = newPreparing.length + newReady.length;
      lastOrderCountRef.current = totalOrders;
      lastOrderIdsRef.current = currentOrderIds;

      setPreparingOrders(newPreparing);
      setReadyOrders(newReady);
      setOnTheWayOrders(newOnTheWay);
    } catch (error) {
      console.error("Error fetching kitchen orders:", error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [handleNewOrders]);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, [fetchOrders, selectedBranch]);

  const handleMarkPreparing = async (orderId) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/status`,
        { status: "preparing" },
        {
          withCredentials: true,
          timeout: 10000
        }
      );
      toast.success("Pedido en preparación");
      await fetchOrders();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error al actualizar estado");
      // Recargar de todos modos por si se actualizó
      await fetchOrders();
    }
  };

  const handleMarkReady = async (orderId) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/status`,
        { status: "ready" },
        {
          withCredentials: true,
          timeout: 10000
        }
      );
      toast.success("¡Pedido listo!");
      await fetchOrders();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error al actualizar estado");
      // Recargar de todos modos por si se actualizó
      await fetchOrders();
    }
  };

  const handleMarkDelivered = async (orderId) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/status`,
        { status: "delivered" },
        {
          withCredentials: true,
          timeout: 10000
        }
      );
      toast.success("Pedido entregado");
      await fetchOrders();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error al actualizar estado");
      // Recargar de todos modos por si se actualizó
      await fetchOrders();
    }
  };

  const handleSendToDelivery = async (orderId) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/status`,
        { status: "on_the_way" },
        {
          withCredentials: true,
          timeout: 10000
        }
      );
      toast.success("Enviado a reparto");
      await fetchOrders();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error al actualizar estado");
      // Recargar de todos modos por si se actualizó
      await fetchOrders();
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchOrders();
  };

  const formatTime = (createdAt) => {
    const timeSinceCreated = Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000);
    if (timeSinceCreated > MAX_TIME_MINUTES) {
      return `+${MAX_TIME_MINUTES}`;
    }
    return timeSinceCreated;
  };

  const OrderCard = ({ order, actions, showDeliveryInfo = false }) => {
    const orderType = order.order_type || "delivery";
    const isLocal = orderType === "local";
    const isPickup = orderType === "pickup";
    const isLocalOrPickup = isLocal || isPickup;
    const timeDisplay = formatTime(order.created_at);
    const isOverTime = timeDisplay === `+${MAX_TIME_MINUTES}`;

    // Badge config based on order type
    const orderTypeBadge = {
      local: { bgClass: "bg-green-900/50", badgeClass: "bg-green-500 text-black", label: "🍽️ LOCAL" },
      pickup: { bgClass: "bg-yellow-900/50", badgeClass: "bg-yellow-500 text-black", label: "🛍️ RECOGER" },
      delivery: { bgClass: "bg-blue-900/50", badgeClass: "bg-blue-500 text-white", label: "🛵 ENTREGA" }
    }[orderType] || { bgClass: "bg-gray-900/50", badgeClass: "bg-gray-500", label: orderType };

    return (
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        <div className={`p-3 ${orderTypeBadge.bgClass} border-b border-gray-700`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="font-heading font-black text-white text-3xl tracking-tighter italic">
                #{order.order_id.slice(-4).toUpperCase()}
              </span>
              <Badge className={`${orderTypeBadge.badgeClass} text-[10px] h-5 px-1`}>
                {orderTypeBadge.label}
              </Badge>
              {order.items.some(item => item.quantity > (item.kitchen_quantity || 0)) && (
                <Badge className="bg-blue-600 text-white text-[10px] h-5 px-1 border-none animate-pulse">
                  ACTUALIZADO
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-700"
                onClick={() => printOrder(order)}
                title="Imprimir comanda"
              >
                <Printer className="w-4 h-4" />
              </Button>
              <div className={`flex items-center gap-1 text-sm ${isOverTime ? "text-red-400" : "text-gray-400"}`}>
                <Clock className="w-4 h-4" />
                {timeDisplay} min
              </div>
            </div>
          </div>
          {isLocal && order.table_number && (
            <p className="text-green-400 font-bold mt-1">Mesa {order.table_number}</p>
          )}
          {isPickup && (
            <p className="text-yellow-400 font-bold mt-1">🛍️ Cliente recoge en tienda</p>
          )}
          {!isLocalOrPickup && order.address && (
            <p className="text-blue-400 text-sm mt-1 truncate flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {order.address.street}
            </p>
          )}
        </div>

        <div className="p-4">
          <div className="space-y-2 mb-4">
            {order.items.map((item, i) => {
              const isModifiedItem = item.quantity > (item.kitchen_quantity || 0);
              return (
                <div key={i} className={`flex justify-between items-start p-1 rounded ${isModifiedItem ? 'bg-blue-900/40 border-l-4 border-blue-500' : ''}`}>
                  <div>
                    <p className="text-white font-medium flex items-center gap-2">
                      <span className="text-yellow-500 font-bold">{item.quantity}x</span>
                      {item.name}
                      {isModifiedItem && (
                        <span className="text-[8px] bg-blue-600 text-white px-1 rounded animate-pulse">NUEVO / EXTRA</span>
                      )}
                    </p>
                    {item.extras?.length > 0 && (
                      <p className="text-gray-400 text-sm pl-6">
                        + {item.extras.map(e => (typeof e === 'string' ? e : e.name)).join(", ")}
                      </p>
                    )}
                    {item.notes && (
                      <p className="text-orange-400 text-sm pl-6 italic">
                        "{item.notes}"
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {order.notes && (
            <div className="bg-gray-900 rounded-lg p-2 mb-4">
              <p className="text-orange-400 text-sm">📝 {order.notes}</p>
            </div>
          )}

          {showDeliveryInfo && order.driver_name && (
            <div className="bg-blue-900/30 rounded-lg p-2 mb-4 border border-blue-800">
              <p className="text-blue-300 text-sm flex items-center gap-1">
                <Truck className="w-4 h-4" />
                Repartidor: <span className="font-bold">{order.driver_name}</span>
              </p>
            </div>
          )}

          {actions && (
            <div className="flex gap-2">
              {actions}
            </div>
          )}
        </div>
      </div>
    );
  };

  const totalActiveOrders = preparingOrders.length + readyOrders.length + onTheWayOrders.length;

  if (isLoading) {
    return (
      <AdminLayout title="Cocina">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((col) => (
            <div key={col} className="space-y-4">
              <div className="h-12 bg-gray-800 rounded-lg animate-pulse" />
              {[1, 2].map((i) => (
                <div key={i} className="h-48 bg-gray-800 rounded-xl animate-pulse" />
              ))}
            </div>
          ))}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Cocina">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-yellow-500 flex items-center gap-2">
              <ChefHat className="w-7 h-7" />
              PANTALLA DE COCINA
            </h1>
            <p className="text-gray-400">
              {totalActiveOrders} pedidos activos
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Branch Selector (Only for Admin or multi-branch users) */}
            {((user?.role === "admin" && (!user?.assigned_branches || user?.assigned_branches.length !== 1)) || (user?.assigned_branches?.length > 1)) && (
              <div className="w-48">
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white h-9">
                    <SelectValue placeholder="Todas las sucursales" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-800 text-white">
                    <SelectItem value="all">Todas las sucursales</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Print Settings Toggle */}
            <div className={`flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2 border ${autoPrintEnabled ? (qzConnected ? 'border-green-500/50' : 'border-yellow-500/50') : 'border-gray-700'}`}>
              <Printer className={`w-4 h-4 ${autoPrintEnabled ? (qzConnected ? "text-green-500" : "text-yellow-500") : "text-gray-500"}`} />
              <span className="text-sm text-gray-400 font-medium">Auto-imprimir</span>
              <Switch
                checked={autoPrintEnabled}
                onCheckedChange={setAutoPrintEnabled}
                className="data-[state=checked]:bg-green-500"
              />
              {autoPrintEnabled && (
                <div className={`w-2 h-2 rounded-full animate-pulse ${qzConnected ? 'bg-green-500' : 'bg-yellow-500'}`} title={qzConnected ? "Impresora Conectada" : "QZ Tray no detectado"} />
              )}
            </div>

            {/* Print Config Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPrintSettings(true)}
              className={`border-gray-700 ${qzConnected ? "text-green-500" : "text-gray-400"}`}
              title="Configurar impresora"
            >
              <Settings className="w-4 h-4" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSoundEnabled(true);
                playNotificationSound();
                toast.success("Sonido activado y probado");
              }}
              className={`border-yellow-500/50 ${soundEnabled ? "text-yellow-500 animate-pulse bg-yellow-500/10" : "text-gray-500"}`}
              title="Probar/Activar Sonido"
            >
              <Volume2 className="w-4 h-4 mr-2" />
              Probar Sonido
            </Button>

            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="border-gray-700 text-gray-300 hover:text-yellow-500 hover:border-yellow-500"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
              Actualizar
            </Button>
          </div>
        </div>


        {/* Kitchen Display - 3 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* EN PREPARACIÓN Column */}
          <div>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-700">
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <ChefHat className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-orange-500 text-lg">EN PREPARACIÓN</h2>
                <p className="text-gray-400 text-sm">{preparingOrders.length} pedidos</p>
              </div>
            </div>

            <div className="space-y-4 max-h-[calc(100vh-320px)] overflow-y-auto pr-1">
              {preparingOrders.length === 0 ? (
                <div className="bg-gray-900 rounded-xl p-8 text-center border border-gray-800">
                  <ChefHat className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-500">Sin pedidos pendientes</p>
                </div>
              ) : (
                preparingOrders.map((order) => (
                  <OrderCard
                    key={order.order_id}
                    order={order}
                    actions={
                      <>
                        {order.status === "pending" && (
                          <Button
                            size="sm"
                            className="flex-1 bg-orange-500 hover:bg-orange-600 text-black font-bold"
                            onClick={() => handleMarkPreparing(order.order_id)}
                          >
                            <ChefHat className="w-4 h-4 mr-1" />
                            Empezar
                          </Button>
                        )}
                        {order.status === "preparing" && (
                          <Button
                            size="sm"
                            className="flex-1 bg-green-500 hover:bg-green-600 text-black font-bold"
                            onClick={() => handleMarkReady(order.order_id)}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            ¡Listo!
                          </Button>
                        )}
                      </>
                    }
                  />
                ))
              )}
            </div>
          </div>

          {/* LISTOS Column */}
          <div>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-700">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Check className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-green-500 text-lg">LISTOS</h2>
                <p className="text-gray-400 text-sm">{readyOrders.length} pedidos</p>
              </div>
            </div>

            <div className="space-y-4 max-h-[calc(100vh-320px)] overflow-y-auto pr-1">
              {readyOrders.length === 0 ? (
                <div className="bg-gray-900 rounded-xl p-8 text-center border border-gray-800">
                  <Check className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-500">Sin pedidos listos</p>
                </div>
              ) : (
                readyOrders.map((order) => {
                  const orderType = order.order_type || "delivery";
                  const isLocal = orderType === "local";
                  const isPickup = orderType === "pickup";
                  const isLocalOrPickup = isLocal || isPickup;
                  return (
                    <OrderCard
                      key={order.order_id}
                      order={order}
                      actions={
                        isLocalOrPickup ? (
                          <Button
                            size="sm"
                            className={`flex-1 font-bold ${isPickup ? "bg-yellow-500 hover:bg-yellow-600 text-black" : "bg-green-500 hover:bg-green-600 text-black"}`}
                            onClick={() => handleMarkDelivered(order.order_id)}
                          >
                            <UtensilsCrossed className="w-4 h-4 mr-1" />
                            {isPickup ? "Entregar al cliente" : "Entregar en mesa"}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold"
                            onClick={() => handleSendToDelivery(order.order_id)}
                          >
                            <Truck className="w-4 h-4 mr-1" />
                            Enviar a reparto
                          </Button>
                        )
                      }
                    />
                  );
                })
              )}
            </div>
          </div>

          {/* ENVIADOS Column */}
          <div>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-700">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Truck className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-blue-500 text-lg">ENVIADOS</h2>
                <p className="text-gray-400 text-sm">{onTheWayOrders.length} pedidos</p>
              </div>
            </div>

            <div className="space-y-4 max-h-[calc(100vh-320px)] overflow-y-auto pr-1">
              {onTheWayOrders.length === 0 ? (
                <div className="bg-gray-900 rounded-xl p-8 text-center border border-gray-800">
                  <Truck className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                  <p className="text-gray-500">Sin pedidos en camino</p>
                </div>
              ) : (
                onTheWayOrders.map((order) => (
                  <OrderCard
                    key={order.order_id}
                    order={order}
                    showDeliveryInfo={true}
                    actions={
                      <Button
                        size="sm"
                        className="flex-1 bg-green-500 hover:bg-green-600 text-black font-bold"
                        onClick={() => handleMarkDelivered(order.order_id)}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Marcar entregado
                      </Button>
                    }
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Print Settings Dialog */}
      <Dialog open={showPrintSettings} onOpenChange={setShowPrintSettings}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-yellow-500 flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Configuración de Impresora
            </DialogTitle>
          </DialogHeader>

          <PrinterSettings />
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
